// ─── Notifications Query & Mutation Hooks ──────────────────────────
// React Query hooks for notification data.
// These replace the useEffect+setInterval pattern in NotificationsContext.

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/queryKeys';
import {
  fetchNotifications,
  markNotificationAsReadAPI,
  markAllNotificationsAsReadAPI,
  deleteNotificationAPI,
  type NotificationItem,
} from '@/lib/api/notifications';

/** Fetch notifications with auto-refresh every 60 seconds. */
export function useNotificationsQuery(token: string | undefined) {
  return useQuery({
    queryKey: queryKeys.notifications.all,
    queryFn: () => fetchNotifications(token!),
    enabled: !!token,
    staleTime: 0,        // Always stale — poll every interval
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 min
    refetchInterval: 60_000, // Poll every 60 seconds
  });
}

/** Mark a single notification as read with optimistic update. */
export function useMarkAsRead(token: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationId: string) => {
      if (!token) throw new Error('No session');
      return markNotificationAsReadAPI(token, notificationId);
    },
    onMutate: async (notificationId) => {
      // Cancel outgoing refetches so they don't overwrite optimistic update
      await queryClient.cancelQueries({ queryKey: queryKeys.notifications.all });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData<{ items: NotificationItem[]; unreadCount: number }>(queryKeys.notifications.all);

      // Optimistically update
      if (previousData) {
        queryClient.setQueryData(queryKeys.notifications.all, {
          ...previousData,
          items: previousData.items.map(n =>
            n.id === notificationId ? { ...n, read: true } : n
          ),
          unreadCount: Math.max(0, previousData.unreadCount - 1),
        });
      }

      return { previousData };
    },
    onError: (_err, _notificationId, context) => {
      // Roll back on error
      if (context?.previousData) {
        queryClient.setQueryData(queryKeys.notifications.all, context.previousData);
      }
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
    },
  });
}

/** Mark all notifications as read with optimistic update. */
export function useMarkAllAsRead(token: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => {
      if (!token) throw new Error('No session');
      return markAllNotificationsAsReadAPI(token);
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: queryKeys.notifications.all });

      const previousData = queryClient.getQueryData<{ items: NotificationItem[]; unreadCount: number }>(queryKeys.notifications.all);

      if (previousData) {
        queryClient.setQueryData(queryKeys.notifications.all, {
          ...previousData,
          items: previousData.items.map(n => ({ ...n, read: true })),
          unreadCount: 0,
        });
      }

      return { previousData };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKeys.notifications.all, context.previousData);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
    },
  });
}

/** Delete a single notification with optimistic update. */
export function useDeleteNotification(token: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationId: string) => {
      if (!token) throw new Error('No session');
      return deleteNotificationAPI(token, notificationId);
    },
    onMutate: async (notificationId) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.notifications.all });

      const previousData = queryClient.getQueryData<{ items: NotificationItem[]; unreadCount: number }>(queryKeys.notifications.all);

      if (previousData) {
        const deleted = previousData.items.find(n => n.id === notificationId);
        queryClient.setQueryData(queryKeys.notifications.all, {
          ...previousData,
          items: previousData.items.filter(n => n.id !== notificationId),
          unreadCount: deleted && !deleted.read
            ? Math.max(0, previousData.unreadCount - 1)
            : previousData.unreadCount,
        });
      }

      return { previousData };
    },
    onError: (_err, _notificationId, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKeys.notifications.all, context.previousData);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
    },
  });
}